import React, { useState } from 'react';

interface InsuranceData {
  coiFile: File | null;
  coiFileName: string;
  autoLiability: string;
  cargoInsurance: string;
  generalLiability: string;
  expirationDate: string;
  insuranceEndorsementFile: File | null;
  insuranceEndorsementFileName: string;
}

interface Step3Props {
  data: InsuranceData;
  onNext: () => void;
  onBack: () => void;
  onSave: (data: InsuranceData) => void;
}

const Step3Insurance: React.FC<Step3Props> = ({ data, onNext, onBack, onSave }) => {
  const [formData, setFormData] = useState<InsuranceData>({
    coiFile: data.coiFile || null,
    coiFileName: data.coiFileName || '',
    autoLiability: data.autoLiability || '',
    cargoInsurance: data.cargoInsurance || '',
    generalLiability: data.generalLiability || '',
    expirationDate: data.expirationDate || '',
    insuranceEndorsementFile: data.insuranceEndorsementFile || null,
    insuranceEndorsementFileName: data.insuranceEndorsementFileName || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'coiFile' | 'insuranceEndorsementFile') => {
    const file = e.target.files?.[0];
    if (file) {
      const fileNameField = field === 'coiFile' ? 'coiFileName' : 'insuranceEndorsementFileName';
      setFormData(prev => ({
        ...prev,
        [field]: file,
        [fileNameField]: file.name,
      }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.coiFile && !formData.coiFileName) {
      newErrors.coiFile = 'Certificate of Insurance is required';
    }
    if (!formData.autoLiability) {
      newErrors.autoLiability = 'Auto liability coverage is required';
    } else if (Number(formData.autoLiability) < 750000) {
      newErrors.autoLiability = 'Minimum $750,000 required';
    }
    if (!formData.cargoInsurance) {
      newErrors.cargoInsurance = 'Cargo insurance is required';
    } else if (Number(formData.cargoInsurance) < 100000) {
      newErrors.cargoInsurance = 'Minimum $100,000 required';
    }
    if (!formData.expirationDate) {
      newErrors.expirationDate = 'Expiration date is required';
    } else if (new Date(formData.expirationDate) < new Date()) {
      newErrors.expirationDate = 'Insurance must not be expired';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Insurance Information</h2>
        <p className="text-gray-600 text-sm">
          Upload your insurance documents and enter coverage amounts.
        </p>
      </div>

      {/* Certificate of Insurance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-3">Certificate of Insurance (COI) *</h3>
        <p className="text-sm text-blue-700 mb-3">
          Broker must be listed as Certificate Holder on your COI.
        </p>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => handleFileChange(e, 'coiFile')}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
        />
        {formData.coiFileName && (
          <p className="mt-2 text-sm text-green-600">✓ {formData.coiFileName}</p>
        )}
        {errors.coiFile && <p className="mt-1 text-sm text-red-600">{errors.coiFile}</p>}
      </div>

      {/* Insurance Endorsement Pages (Optional) */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">Insurance Endorsement Pages (Optional)</h3>
        <p className="text-sm text-gray-600 mb-3">
          Additional endorsement pages showing coverage details.
        </p>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => handleFileChange(e, 'insuranceEndorsementFile')}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-white hover:file:bg-gray-700"
        />
        {formData.insuranceEndorsementFileName && (
          <p className="mt-2 text-sm text-green-600">✓ {formData.insuranceEndorsementFileName}</p>
        )}
      </div>

      {/* Coverage Amounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Auto Liability Coverage *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              name="autoLiability"
              value={formData.autoLiability}
              onChange={handleChange}
              placeholder="750000"
              className={`w-full pl-7 pr-3 py-2 border rounded-md ${errors.autoLiability ? 'border-red-500' : 'border-gray-300'}`}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Minimum $750,000</p>
          {errors.autoLiability && <p className="text-sm text-red-600">{errors.autoLiability}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cargo Insurance *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              name="cargoInsurance"
              value={formData.cargoInsurance}
              onChange={handleChange}
              placeholder="100000"
              className={`w-full pl-7 pr-3 py-2 border rounded-md ${errors.cargoInsurance ? 'border-red-500' : 'border-gray-300'}`}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Minimum $100,000</p>
          {errors.cargoInsurance && <p className="text-sm text-red-600">{errors.cargoInsurance}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            General Liability (Optional)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              name="generalLiability"
              value={formData.generalLiability}
              onChange={handleChange}
              placeholder="1000000"
              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Insurance Expiration Date *
          </label>
          <input
            type="date"
            name="expirationDate"
            value={formData.expirationDate}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md ${errors.expirationDate ? 'border-red-500' : 'border-gray-300'}`}
          />
          {errors.expirationDate && <p className="text-sm text-red-600">{errors.expirationDate}</p>}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Next
        </button>
      </div>
    </form>
  );
};

export default Step3Insurance;